import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Add,
  AccountBalanceWallet,
  TrendingUp,
  Savings,
  Refresh,
  Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { cryptoService, PortfolioData, CryptoData, OverviewData } from '../../services/cryptoService';
import { authService } from '../../services/authService';
import { androidColors } from '../../theme/androidTheme';
import Layout from '../../components/Layout/Layout';
import OverviewCard from '../../components/OverviewCard/OverviewCard';
import CryptoListItem from '../../components/CryptoListItem/CryptoListItem';
import AllocationChart from '../../components/Charts/AllocationChart';
import OverviewChart from '../../components/Charts/OverviewChart';
import PageTitle from '../../components/PageTitle/PageTitle';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portfolioData, setPortfolioData] = useState<PortfolioData>({
    cryptos: []
  });
  const [overviewData, setOverviewData] = useState<OverviewData>({
    totalCapital: 0,
    gain: 0,
    gainPercentage: 0,
    stackedIncome: 0,
    airdropIncome: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      const [portfolioResult, overviewResult] = await Promise.all([
        cryptoService.getPortfolioData(),
        cryptoService.getOverviewData()
      ]);
      setPortfolioData(portfolioResult);
      setOverviewData(overviewResult);
    } catch (err: any) {
      console.error('Erreur dashboard:', err);
      setError(err.message || 'Erreur lors du chargement des données');
      
      if (err.message?.includes('Session expirée') || err.message?.includes('Token JWT manquant')) {
        authService.logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCrypto = () => {
    navigate('/portfolio');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'fr' ? 'fr-FR' : undefined, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout title={t('dashboard.title')}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress sx={{ color: androidColors.primaryGold }} />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={t('dashboard.title')}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <Alert
            severity="error"
            sx={{
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: androidColors.error,
              '& .MuiAlert-icon': {
                color: androidColors.error,
              },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={loadDashboardData}
                startIcon={<Refresh />}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title={t('dashboard.title')}>
      <PageTitle title={t('dashboard.title')} />
      {/* Cartes de vue d'ensemble */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
          <OverviewCard
            title={t('dashboard.totalCapital')}
            value={formatCurrency(overviewData.totalCapital)}
            change={overviewData.gainPercentage.toFixed(2)}
            isPositive={overviewData.gain >= 0}
            icon={AccountBalanceWallet}
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
          <OverviewCard
            title={t('dashboard.gainLoss')}
            value={`${overviewData.gain >= 0 ? '+' : ''}${formatCurrency(overviewData.gain)}`}
            change={overviewData.gainPercentage.toFixed(2)}
            isPositive={overviewData.gain >= 0}
            icon={TrendingUp}
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
          <OverviewCard
            title={t('dashboard.stakedIncome')}
            value={formatCurrency(overviewData.stackedIncome)}
            change="0.00"
            isPositive={overviewData.stackedIncome >= 0}
            icon={Savings}
          />
        </Box>
      </Box>

      {/* Graphiques */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(66.666% - 12px)' } }}>
          <OverviewChart overviewData={overviewData} />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(33.333% - 12px)' } }}>
          <AllocationChart cryptos={portfolioData.cryptos} />
        </Box>
      </Box>

      {/* Mes cryptomonnaies */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
          }}
        >
          {t('dashboard.myCryptos')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={handleAddCrypto}
          sx={{
            backgroundColor: androidColors.primaryGold,
            color: androidColors.darkBackground,
            '&:hover': {
              backgroundColor: androidColors.darkGold,
            },
          }}
        >
          {t('common.edit')}
        </Button>
      </Box>

      {/* Liste des cryptomonnaies */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {portfolioData.cryptos.length > 0 ? (
          portfolioData.cryptos.map((crypto: CryptoData, index: number) => (
            <CryptoListItem
              key={`${crypto.symbol}-${index}`}
              crypto={crypto}
              index={index}
              onEdit={() => navigate('/edit-crypto', { state: { crypto, index } })}
              onDelete={async () => {
                try {
                  await cryptoService.removeCryptoFromPortfolio(index);
                  await loadDashboardData();
                } catch (err) {
                  console.error('Erreur lors de la suppression:', err);
                }
              }}
              isDashboard={true}
            />
          ))
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: androidColors.textSecondary,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('dashboard.noCryptos')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {t('dashboard.addFirstCrypto')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCrypto}
              sx={{
                backgroundColor: androidColors.primaryGold,
                color: androidColors.darkBackground,
                '&:hover': {
                  backgroundColor: androidColors.darkGold,
                },
              }}
            >
              {t('dashboard.addCryptoCta')}
            </Button>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default Dashboard;